import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  faArrowLeft,
  faCalendarAlt,
  faDownload,
  faExchangeAlt,
  faFileCode,
  faFileExport,
  faFileInvoice,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";

import useMiniERPExportAPI from "./components/MiniERPExportAPI";

const MiniERPExport = () => {
  const navigate = useNavigate();

  const authState = useSelector((state) => state.authReducer || {});
  const { exportJSON, exportTally } = useMiniERPExportAPI();

  const token = authState?.token;

  const currentFY = useMemo(() => {
    const d = new Date();
    const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
    return `${y}-${String(y + 1).slice(-2)}`;
  }, []);

  const [form, setForm] = useState({
    companyName: authState?.user?.remarks || "",
    exportedBy: authState?.user?.name || "",
    financialYear: currentFY,
    voucherType: "All",
    exportType: "json",
    fromDate: "",
    toDate: "",
    includeLedgers: true,
    includeInventory: true,
    includeVouchers: true,
  });

  const voucherTypes = [
    "All",
    "Receipt",
    "Payment",
    "Journal",
    "Contra",
    "DebitNote",
    "CreditNote",
  ];

  const financialYears = [
    "2024-25",
    "2025-26",
    "2026-27",
    "2027-28",
    "2028-29",
  ];

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  return (
    <div className="min-h-screen bg-black text-white p-4">
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate(-1)}
          className="w-fit px-2 py-1 rounded-lg bg-zinc-900 hover:bg-zinc-800"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Back
        </button>

        <h1 className="text-2xl font-bold">
          <FontAwesomeIcon icon={faFileExport} className="mr-3 text-cyan-400" />
          MiniERP Export Center
        </h1>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
          <div className="space-y-4">
            <input
              className="w-full bg-black border border-zinc-700 rounded-lg p-3"
              placeholder="Company Name"
              name="companyName"
              value={form.companyName}
              onChange={handleChange}
            />

            <input
              className="w-full bg-black border border-zinc-700 rounded-lg p-3"
              placeholder="Exported By"
              name="exportedBy"
              value={form.exportedBy}
              onChange={handleChange}
            />

            <select
              className="w-full bg-black border border-zinc-700 rounded-lg px-2 py-1"
              name="financialYear"
              value={form.financialYear}
              onChange={handleChange}
            >
              {financialYears.map((fy) => (
                <option key={fy}>{fy}</option>
              ))}
            </select>

            <div className="grid grid-cols-2 gap-3">
              <input
                type="date"
                className="bg-black border border-zinc-700 rounded-lg p-3"
                name="fromDate"
                value={form.fromDate}
                onChange={handleChange}
              />

              <input
                type="date"
                className="bg-black border border-zinc-700 rounded-lg p-3"
                name="toDate"
                value={form.toDate}
                onChange={handleChange}
              />
            </div>

            <select
              className="w-full bg-black border border-zinc-700 rounded-lg px-2 py-1"
              name="voucherType"
              value={form.voucherType}
              onChange={handleChange}
            >
              {voucherTypes.map((v) => (
                <option key={v}>{v}</option>
              ))}
            </select>

            <div className="flex gap-6">
              <label>
                <input
                  type="radio"
                  name="exportType"
                  value="json"
                  checked={form.exportType === "json"}
                  onChange={handleChange}
                />

                <span className="ml-2">JSON</span>
              </label>

              <label>
                <input
                  type="radio"
                  name="exportType"
                  value="tally"
                  checked={form.exportType === "tally"}
                  onChange={handleChange}
                />

                <span className="ml-2">Tally XML</span>
              </label>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <label>
                <input
                  type="checkbox"
                  name="includeLedgers"
                  checked={form.includeLedgers}
                  onChange={handleChange}
                />
                <span className="ml-2">Ledgers</span>
              </label>

              <label>
                <input
                  type="checkbox"
                  name="includeInventory"
                  checked={form.includeInventory}
                  onChange={handleChange}
                />
                <span className="ml-2">Inventory</span>
              </label>

              <label>
                <input
                  type="checkbox"
                  name="includeVouchers"
                  checked={form.includeVouchers}
                  onChange={handleChange}
                />
                <span className="ml-2">Vouchers</span>
              </label>
            </div>
          </div>
        </div>

        <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
          <div className="space-y-4">
            <button className="w-fit px-2 py-1 bg-cyan-600 hover:bg-cyan-700 rounded-lg">
              <FontAwesomeIcon icon={faExchangeAlt} className="mr-2" />
              Preview Export
            </button>

            <button
              onClick={async () => {
                const blob = await exportJSON(
                  {
                    userId: authState.user._id,
                    ...form,
                  },
                  token,
                );

                if (!blob) return;

                const url = window.URL.createObjectURL(blob);

                const a = document.createElement("a");

                a.href = url;

                a.download = `MiniERP_${new Date().toISOString().slice(0, 10)}.json`;

                a.click();

                window.URL.revokeObjectURL(url);
              }}
              className="w-fit px-2 py-1 bg-emerald-600 hover:bg-emerald-700 rounded-lg"
            >
              <FontAwesomeIcon icon={faFileCode} className="mr-2" />
              Export JSON
            </button>

            <button
              onClick={async () => {
                const blob = await exportTally(
                  {
                    userId: authState.user._id,
                    ...form,
                  },
                  token,
                );

                if (!blob) return;

                const url = window.URL.createObjectURL(blob);

                const a = document.createElement("a");

                a.href = url;

                a.download = `MiniERP_${new Date().toISOString().slice(0, 10)}.xml`;

                a.click();

                window.URL.revokeObjectURL(url);
              }}
              className="w-fit px-2 py-1 bg-amber-600 hover:bg-amber-700 rounded-lg"
            >
              <FontAwesomeIcon icon={faFileInvoice} className="mr-2" />
              Export Tally XML
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MiniERPExport;
